import React from 'react';
import { FactMetadataOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import {
  appendChildAt,
  ComparisonNode,
  ConditionNode,
  emptyComparison,
  isComparison,
  isGroup,
  isNot,
  NodePath,
  removeNodeAt,
  toggleGroupOpAt,
  toggleNotAt,
  updateNodeAt,
  wrapInGroupAt,
} from '../../ruleAst';
import ConditionLeaf from './ConditionLeaf';

interface ComponentProps {
  condition?: ConditionNode;
  facts: FactMetadataOutDTO[];
  onChange: (condition?: ConditionNode) => void;
  companyId?: string;
  readOnly?: boolean;
}

/**
 * The condition tree: nested AND/OR groups over comparison leaves.
 *
 * Nodes are addressed by path rather than by id, because the wire format has no id and adding a
 * synthetic one would have to be stripped from every request. See `ruleAst.ts`.
 */
const ConditionTree: React.FC<ComponentProps> = ({ condition, facts, onChange, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();

  if (!condition) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-dashed border-base-tertiary p-3">
        <span className="text-content-secondary">{tr('AiDispatcher.noCondition', 'No condition — the rule fires every time the trigger occurs.')}</span>
        {!readOnly && <RingilButton mode="text-only" label={tr('AiDispatcher.addCondition', 'Add a condition')} onClick={() => onChange(emptyComparison())} />}
      </div>
    );
  }

  const update = (path: NodePath, updater: (node: ConditionNode) => ConditionNode) => onChange(updateNodeAt(condition, path, updater));
  const remove = (path: NodePath) => onChange(removeNodeAt(condition, path));
  const wrap = (path: NodePath, op: 'AND' | 'OR') => onChange(wrapInGroupAt(condition, path, op));
  const negate = (path: NodePath) => onChange(toggleNotAt(condition, path));
  const append = (path: NodePath) => onChange(appendChildAt(condition, path, emptyComparison()));
  const flipGroup = (path: NodePath) => onChange(toggleGroupOpAt(condition, path));

  /**
   * `negatedAt` is the path of the NOT wrapping this node, when it has one. A negated leaf renders
   * as a badge on the leaf itself, so its "not" button has to target the wrapper — targeting the
   * leaf would nest a second NOT instead of removing the one that is there.
   */
  const renderNode = (node: ConditionNode, path: NodePath, negatedAt?: NodePath): React.ReactNode => {
    if (isComparison(node)) {
      return (
        <ConditionLeaf
          node={node}
          facts={facts}
          onChange={next => update(path, () => next as ComparisonNode)}
          onRemove={() => remove(path)}
          onWrap={op => wrap(path, op)}
          onToggleNot={() => negate(negatedAt ?? path)}
          negated={Boolean(negatedAt)}
          companyId={companyId}
          readOnly={readOnly}
        />
      );
    }

    if (isNot(node)) {
      // A NOT around a leaf renders as a badge on the leaf; around a group it wraps the group.
      if (isComparison(node.child)) return renderNode(node.child, [...path, 0], path);
      return (
        <div className="rounded-md border-2 border-solid border-base-tertiary p-2">
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded bg-base-tertiary px-2 py-0.5 text-xs font-medium uppercase text-content-secondary">{tr('AiDispatcher.not', 'not')}</span>
            {!readOnly && <RingilButton mode="text-only" label={tr('AiDispatcher.removeNegation', 'Remove negation')} onClick={() => negate(path)} />}
          </div>
          {renderNode(node.child, [...path, 0])}
        </div>
      );
    }

    if (isGroup(node)) {
      return (
        <div className="rounded-md border border-solid border-base-tertiary bg-base-secondary p-2">
          <div className="mb-2 flex items-center gap-2">
            <RingilButton
              mode={readOnly ? 'text-only' : 'table'}
              label={node.op === 'AND' ? tr('AiDispatcher.allOf', 'All of') : tr('AiDispatcher.anyOf', 'Any of')}
              tooltip={tr('AiDispatcher.flipGroupTooltip', 'Switch between AND and OR')}
              onClick={() => (readOnly ? undefined : flipGroup(path))}
              disabled={readOnly}
            />
            {!readOnly && (
              <>
                <RingilButton mode="text-only" label={tr('AiDispatcher.addToGroup', '+ condition')} onClick={() => append(path)} />
                {path.length > 0 && <RingilButton mode="text-only" label={tr('AiDispatcher.removeGroup', 'Remove group')} onClick={() => remove(path)} />}
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 pl-3">{node.children.map((child, index) => <div key={index}>{renderNode(child, [...path, index])}</div>)}</div>
        </div>
      );
    }

    // Unknown node kind — the backend added an operator we do not render yet. Show it rather than
    // silently dropping it, since it is still part of the rule.
    return <pre className="overflow-x-auto rounded bg-base-tertiary p-2 text-xs">{JSON.stringify(node, null, 2)}</pre>;
  };

  return <div className="flex flex-col gap-2">{renderNode(condition, [])}</div>;
};

export default ConditionTree;
